import React, { useState } from 'react';
import {useLocation, Navigate, useNavigate, useParams} from 'react-router-dom';

import validateIntermezzo from '../validators/validateIntermezzo';
import validateNumber from "../validators/validateNumber";
import validateText from "../validators/validateText";
import validateNumberMultiple from "../validators/validateNumberMultiple";
import validateOptionMultiple from "../validators/validateOptionMultiple";
import validateOptionSingle from "../validators/validateOptionSingle";
import validateTextMultiple from "../validators/validateTextMultiple";

import Questionnaire from './Questionnaire';
import useAssessment from '../hooks/useAssessment';
import Question from "./Question";
import {LanguageProvider, useLanguage} from "./LanguageProvider";
import ErrorMessage from "./ErrorMessage";
import {QuestionList} from "./QuestionList";
import QuestionSubmitButton from "./QuestionSubmitButton";
import {useMutation} from "@apollo/client";
import {
  ASSESSMENT_ANSWERS,
  SAVE_ASSESSMENT_ANSWERS,
  VALIDATE_ASSESSMENT_ANSWERS
} from "../../apollo/queries";
import {client} from "../../apollo/client";
import QuestionBackButton from "./QuestionBackButton";
import {getFirstNonSkipQuestionNumber, getPreviousNonSkipQuestionNumber, shouldSkipQuestion} from "../logic/assessment";


const Assessment = () => {
  const [error, setError] = useState({});
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false)
  const params = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation();

  const questionNumber = params.questionNumber === undefined || params.questionNumber === 'undefined' ? undefined : parseInt(params.questionNumber, 10);
  const { assessmentId } = params;

  const { error: assessmentError, assessment } = useAssessment(assessmentId, questionNumber);
  const [validateAssessmentAnswers] = useMutation(VALIDATE_ASSESSMENT_ANSWERS)
  const [saveUpdatedAnswers] = useMutation(SAVE_ASSESSMENT_ANSWERS)

  const language = useLanguage();

  const options = {};
  let content;

  if (assessment) {
    const { info, data, saveAnswers } = assessment;
    if (assessment.currentQuestionNumber !== questionNumber && questionNumber !== 999) {
      console.log(`Number mismatch redirect, current: ${assessment.currentQuestionNumber}, questionNumber: ${questionNumber}`)
      let redirect = `/assessments/assessment/${assessmentId}/${assessment.currentQuestionNumber}`;
      if (questionNumber === undefined) redirect = `${pathname.replace(/\/$/, '')}/${assessment.currentQuestionNumber}`;
      return <Navigate to={redirect} replace/>;
    }

    const question = info.questions[assessment.currentQuestionNumber === 0 ? 0 : assessment.currentQuestionNumber - 1];

    let questionListAnswers = data.answers
    content = (
        <div style={{margin: 'auto', marginTop: "20px", marginBottom: "20px"}} className={'box'}>
          <QuestionList questions={info.questions} nextButton={false} answers={data.answers} getUpdatedAnswers={(answers) => {
            questionListAnswers = answers
          }}></QuestionList>
          <div style={{    position: 'fixed', top: '50%',right: '3%'}}>
            <button type="submit" onClick={() => {
              validateAssessmentAnswers({
                variables: {
                  input: {
                    assessmentUuid: assessmentId,
                    answers: questionListAnswers,
                    version: 0
                  }
                },
                fetchPolicy: 'network-only'
              }).then(data => {
                const { code, firstUnansweredQuestion } = data.data.validateAssessmentAnswers

                if(code === 500) {
                  client.refetchQueries({
                    include: [ASSESSMENT_ANSWERS]
                  })
                  const redirect = pathname.replace(/\/[^\/]*$/, `/${firstUnansweredQuestion + 1}`)
                  window.location.href = redirect

                } else {
                  saveUpdatedAnswers({
                    variables: {
                      input: {
                        assessmentUuid: assessmentId,
                        answers: questionListAnswers,
                        version: 0
                      }
                    },
                    fetchPolicy: 'network-only'
                  });
                  const redirect = pathname.replace(/\/[^\/]*$/, `/${info.questions.length}`)
                  navigate(redirect)
                }
              })
            }} className={`button is-primary brand-primary is-medium is-fullwidth`}>Afronden</button>
          </div>
        </div>
    )
    if(questionNumber !== 999) {
      const categoryQuestions = assessment.info.questions.filter(q => q.category === question.category);
      const isFirst = assessment.info.questions[0].key === question.key;
      const isLast = assessment.info.questions[assessment.info.questions.length - 1].key === question.key;

      const onAnswerAndBack = async () => {
        await saveAnswers(answers);
        const previousQuestionNumber = getPreviousNonSkipQuestionNumber(info.questions, answers, info.questions.indexOf(categoryQuestions[0])) + 1;
        const redirect = pathname.replace(/\/[^\/]*$/, `/${previousQuestionNumber}`)
        console.log(`onAnswerBack redirecting to: ${redirect}`)
        navigate(redirect)
      };

      const handleSubmit = async event => {
        event.preventDefault();
        setIsSubmitting(true);
        if (event.nativeEvent.submitter.id === 'next') {
          event.preventDefault();
          await onSubmit();
        } else {
          await onBack(true);
        }
        setIsSubmitting(false);
      };

      let hasErrors = false;

      const onSubmit = async () => {
        const errors = {}
        setError(errors);
        for (const question of categoryQuestions) {
          let answer = answers[question.key] ?? '';
          if (shouldSkipQuestion(question, answers)) continue;
          let validate = null;
          switch (question.type) {
            case 'QuestionTypeIntermezzo':
              validate = validateIntermezzo;
              break;
            case 'QuestionTypeNumber':
              validate = validateNumber;
              break;
            case 'QuestionTypeNumberMultiple':
              validate = validateNumberMultiple;
              break;
            case 'QuestionTypeOptionMultiple':
              validate = validateOptionMultiple;
              break;
            case 'QuestionTypeOptionSingle':
              validate = validateOptionSingle;
              break;
            case 'QuestionTypeText':
            case 'QuestionTypeTextArea':
              validate = validateText;
              break;
            case 'QuestionTypeTextMultiple':
              validate = validateTextMultiple;
              break;
            default:
              throw new Error(`Unknown question type '${question.type}'`);
          }
          try {
            validate(question, answer, language);
          } catch (err) {
            errors[question.key] = err;
            hasErrors = true;
          }
        }
        if (hasErrors) {
          setError(errors);
          return false;
        }
        await saveAnswers(answers);

        let nextQuestionNumber = getFirstNonSkipQuestionNumber(
            info.questions,
            answers,
            info.questions.indexOf(categoryQuestions[categoryQuestions.length - 1]) + 2
        );

        const nextQuestion = info.questions[nextQuestionNumber - 1];
        if(nextQuestion.key === 'ending') {
          nextQuestionNumber = 999
        }

        const redirect = pathname.replace(/\/[^\/]*$/, `/${nextQuestionNumber}`)
        console.log(`redirecting to: ${redirect}`)
        navigate(redirect)
        return true;
      };

      if (Object.entries(answers).length === 0 && Object.entries(data.answers).length > 0) {
        setAnswers(data.answers);
      }

      const onChangeCallback = (key, answer, validate, validateData, question) => {
        delete error[key];
        try {
          if (undefined === validateData) {
            validate(question, answer, language)
          } else {
            validate(question, validateData, language)
          }

        } catch (err) {
          error[key] = err;
        }
        setError(error);

        setAnswers({ ...answers, [key]: answer });
      };

      const onBack = async () => {
        // if(answer === '') return false;
        await onAnswerAndBack();

        return true;
      };

      Object.assign(options, info.options);
      content = (
          <div className={'box'} style={{ marginTop: "20px", marginBottom: "20px" }}>
            <form onSubmit={handleSubmit} noValidate>
              {categoryQuestions.map(
                  (question, index) => <Question
                      focus={index === 0}
                      key={question.key}
                      data={question}
                      variables={answers}
                      show={!shouldSkipQuestion(question, answers)}
                      onChangeCallback={onChangeCallback}
                      validationError={error[question.key]}
                  />
              )}
              {!isLast && (
                  <>
                    <QuestionSubmitButton caption={data.button} loading={isSubmitting} />
                    {!isFirst && <QuestionBackButton loading={isSubmitting} />}
                  </>
              )}
            </form>
          </div>
    );
    }
    } else if (assessmentError) {
    content = <ErrorMessage error={'Error retrieving assessment'} />;
    console.log(assessmentError)

    // Instead of showing an error, let's just redirect to the parent endpoint.
    // return <Navigate push to={`/${endpointUrlPath}`} />;
  }

  return (
    <LanguageProvider language={assessment && assessment.info && assessment.info.language}>
      <Questionnaire
        title={options.assessmentTitle}
        colorPrimary={options.assessmentColorPrimary}
        colorSecondary={options.assessmentColorSecondary}
        numberOfQuestions={assessment && assessment.info.questions.length}
        currentQuestionNumber={assessment && assessment.currentQuestionNumber}
        firstUnansweredQuestionNumber={assessment && assessment.firstUnansweredQuestionNumber}
      >
        {content}
      </Questionnaire>
    </LanguageProvider>
  );
};

export default Assessment;
